import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useIsMobile } from "@/hooks/use-mobile"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"

/**
 * OKS-19 F9 — BlocklistConfirmation modal (REQUIRED reason variant).
 *
 * **OKS-Admin-only affordance** (UX Spec joint freeze + BE TRD §Key
 * Decisions). The button (`candidate-detail-blocklist`) is rendered
 * by F7's CandidateActionBar only when `actions.blocklist_url !== null`,
 * which BE serializes only for OKS-Admin AND status != "blocklisted".
 * The hide-when-null rule (single visibility discriminator) is the
 * sole mechanism — there is no role-check here.
 *
 * **Required reason** (UX Spec §Modals — joint freeze with ux-oks19):
 * blocklist is destructive cross-brand (the creator gets hidden from
 * approval workflows for every brand on the platform). The reason
 * field is internal-only — never shown to brand or agency users on
 * any surface (P0 brand-isolation invariant; QA E11 marquee covers
 * the leak boundary). Submit is disabled until reason has ≥1
 * non-whitespace char.
 *
 * **Wire format (BE TRD lock — nested strong params):**
 *   POST /candidates/:id/transitions
 *   body: { transition: { event: "blocklist", reason: "..." } }
 *
 * BE validates reason presence at the service layer and returns 422
 * with `errors.reason: "can't be blank"` if missing — surfaced via
 * Inertia's render-prop `errors` object as inline validation copy.
 *
 * **Locked testids (FE TRD §testid Map):**
 *   - `candidate-detail-blocklist-dialog` — modal root
 *   - `candidate-detail-blocklist-reason` — required textarea
 *   - `candidate-detail-blocklist-reason-error` — inline error region
 *   - `candidate-detail-blocklist-confirm` — destructive submit
 *   - `candidate-detail-blocklist-cancel` — ghost cancel
 */
const MAX_REASON_LENGTH = 1000
const SOFT_REASON_LIMIT = 300
const ARIA_ANNOUNCE_THRESHOLD = 240

export interface BlocklistConfirmationProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  actionUrl: string
  onSuccess?: () => void
}

export function BlocklistConfirmation({
  open,
  onOpenChange,
  actionUrl,
  onSuccess,
}: BlocklistConfirmationProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const online = useOnline()

  const [reason, setReason] = useState("")
  const reasonLength = reason.length
  const isOverSoftLimit = reasonLength >= SOFT_REASON_LIMIT
  const shouldAnnounce = reasonLength >= ARIA_ANNOUNCE_THRESHOLD
  // Required-reason gate: submit disabled until ≥1 non-whitespace char.
  const reasonValid = reason.trim().length > 0

  const formContent = (
    <Form
      action={actionUrl}
      method="post"
      options={{ preserveScroll: true }}
      onSuccess={() => {
        setReason("")
        onSuccess?.()
      }}
    >
      {({ errors, processing }) => (
        <div className="flex flex-col gap-4">
          <input type="hidden" name="transition[event]" value="blocklist" />
          <div className="flex flex-col gap-2">
            <Label htmlFor="candidate-detail-blocklist-reason-input">
              {t("candidates.detail.actions.blocklist_reason_label")}
              <span className="text-destructive ml-0.5" aria-hidden="true">
                *
              </span>
            </Label>
            <Textarea
              id="candidate-detail-blocklist-reason-input"
              data-testid="candidate-detail-blocklist-reason"
              name="transition[reason]"
              rows={4}
              maxLength={MAX_REASON_LENGTH}
              placeholder={t(
                "candidates.detail.actions.blocklist_reason_placeholder",
              )}
              aria-required="true"
              aria-invalid={errors.reason ? "true" : undefined}
              aria-describedby={
                errors.reason
                  ? "candidate-detail-blocklist-reason-error"
                  : undefined
              }
              disabled={processing}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="flex items-start justify-between gap-3">
              <p className="text-muted-foreground text-xs">
                {t("candidates.detail.actions.blocklist_reason_helper")}
              </p>
              <p
                className={cn(
                  "shrink-0 text-xs tabular-nums",
                  isOverSoftLimit
                    ? "text-destructive font-medium"
                    : "text-muted-foreground",
                )}
                aria-live={shouldAnnounce ? "polite" : "off"}
              >
                {t("candidates.detail.actions.blocklist_reason_counter", {
                  count: reasonLength,
                  limit: SOFT_REASON_LIMIT,
                })}
              </p>
            </div>
            {/*
              Inline error region — shown only when BE returns
              `errors.reason` (e.g. blank reason despite client-side
              guard, or reason too short server-side). role="alert"
              announces immediately to screen readers.
            */}
            {errors.reason && (
              <p
                id="candidate-detail-blocklist-reason-error"
                data-testid="candidate-detail-blocklist-reason-error"
                role="alert"
                className="text-destructive text-xs"
              >
                {errors.reason}
              </p>
            )}
          </div>
          {isMobile ? (
            <DrawerFooter className="px-0">
              <Button
                type="submit"
                variant="destructive"
                data-testid="candidate-detail-blocklist-confirm"
                disabled={processing || !online || !reasonValid}
              >
                {t("candidates.detail.actions.blocklist_confirm")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                data-testid="candidate-detail-blocklist-cancel"
                disabled={processing}
                onClick={() => onOpenChange(false)}
              >
                {t("candidates.detail.actions.blocklist_cancel")}
              </Button>
            </DrawerFooter>
          ) : (
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                data-testid="candidate-detail-blocklist-cancel"
                disabled={processing}
                onClick={() => onOpenChange(false)}
              >
                {t("candidates.detail.actions.blocklist_cancel")}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                data-testid="candidate-detail-blocklist-confirm"
                disabled={processing || !online || !reasonValid}
              >
                {t("candidates.detail.actions.blocklist_confirm")}
              </Button>
            </DialogFooter>
          )}
        </div>
      )}
    </Form>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent data-testid="candidate-detail-blocklist-dialog">
          <DrawerHeader>
            <DrawerTitle>
              {t("candidates.detail.actions.blocklist_dialog_title")}
            </DrawerTitle>
            <DrawerDescription>
              {t("candidates.detail.actions.blocklist_dialog_description")}
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4">{formContent}</div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="candidate-detail-blocklist-dialog"
        className="sm:max-w-[560px]"
      >
        <DialogHeader>
          <DialogTitle>
            {t("candidates.detail.actions.blocklist_dialog_title")}
          </DialogTitle>
          <DialogDescription>
            {t("candidates.detail.actions.blocklist_dialog_description")}
          </DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  )
}
